import _ from 'lodash';
import constants from '$$helpers/constants';
import rights from '../../../components/rights';

export default function (instance) {
  const strToDate = instance.date.str_to_date(instance.config.date_format);

  instance.__scaleTask = function (task) {
    const copy = instance.copy(task);

    if (instance.config.durationData.mode !== 'hour') {
      const calendar = instance.getCalendar(task.gantt_id);

      copy.start_date = instance.scaleWeekDate(instance.scaleDayDate(task.start_date, calendar));
      copy.end_date = instance.scaleWeekDate(instance.scaleDayDate(task.end_date, calendar));
    }

    return copy;
  };

  instance._checkTimeout = function (host, updPerSecond) {
    if (!updPerSecond) {
      return true;
    }

    if (host._on_timeout) {
      return false;
    }

    const timeout = Math.ceil(1000 / updPerSecond);

    if (timeout < 2) {
      return true;
    }

    setTimeout(() => {
      delete host._on_timeout;
    }, timeout);

    host._on_timeout = true;

    return true;
  };

  instance.getWeekDays = function (_date) {
    const date = _.isString(_date) ? strToDate(_date) : new Date(_date);
    const weekStart = instance.date.week_start(date);
    const weekEnd = instance.date.add(weekStart, 7, 'day');

    const workStart = instance.getClosestWorkTime({
      date: weekStart,
      dir: 'future',
      unit: instance.config.duration_unit,
    });
    const workEnd = instance.getClosestWorkTime({
      date: weekEnd,
      dir: 'past',
      unit: instance.config.duration_unit,
    });

    if (instance.date.week_start(new Date(workStart))
      .valueOf() !== instance.date.week_start(new Date(workEnd))
      .valueOf()) {
      return {
        firstDay: 0,
        lastDay: 0,
        daysPerWeek: 0,
        workStart,
        workEnd,
      };
    }

    let firstDay = 0;
    let lastDay = 0;

    if (instance.config.start_on_monday) {
      firstDay = workStart.getDay() - 1;
      lastDay = workEnd.getDay() - 1;

      firstDay = firstDay < 0 ? 6 : firstDay;
      lastDay = lastDay < 0 ? 6 : lastDay;
    } else {
      firstDay = workStart.getDay();
      lastDay = workEnd.getDay();
    }

    return {
      firstDay,
      lastDay,
      daysPerWeek: lastDay - firstDay + 1,
      workStart,
      workEnd,
    };
  };

  instance.scaleWeekDate = function (_date) {
    if (!_date || instance.getState().scale_unit !== 'week') {
      return _date;
    }

    const date = _.isString(_date) ? strToDate(_date) : new Date(_date);
    const workHours = instance.getWeekDays(date);

    if (!workHours.daysPerWeek) {
      return date;
    }

    let startDayOfWeek = 0;

    if (instance.config.start_on_monday) {
      startDayOfWeek = date.getDay() - 1;

      startDayOfWeek = startDayOfWeek < 0 ? 6 : startDayOfWeek;
    } else {
      startDayOfWeek = date.getDay();
    }

    const diffDate = startDayOfWeek - workHours.firstDay;

    let newDay = (diffDate / workHours.daysPerWeek) * 7;

    newDay = newDay < 0 ? 6 : newDay;
    newDay = newDay * 1000 * 60 * 60 * 24;

    let result = new Date(date);

    result = instance.date.week_start(result);
    result = new Date(result.valueOf() + newDay);

    result = instance.date.add(result, date.getHours(), 'hour');

    return result;
  };

  instance.scaleDayDate = function (_date, calendar) {
    const date = _.isString(_date) ? strToDate(_date) : new Date(_date);
    const workHours = instance.getDayHours(date, calendar);

    if (!date || !workHours.hoursPerDay) {
      return date;
    }

    const startHour = date.getHours();
    let newHour = ((startHour - workHours.firstHour) / workHours.hoursPerDay) * 24;

    if (newHour < 0) {
      newHour = 0;
    }

    const result = new Date(date);

    result.setHours(newHour);

    return result;
  };

  instance.getDayHours = function (_date, calendar) {
    const date = _.isString(_date) ? strToDate(_date) : new Date(_date);
    const dayStart = instance.date.day_start(date);
    const dayEnd = instance.date.add(dayStart, 1, 'day');

    const workStart = instance.getClosestWorkTime({
      date: dayStart,
      dir: 'future',
      unit: instance.config.duration_unit,
      calendar,
    });
    let workEnd = instance.getClosestWorkTime({
      date: dayEnd,
      dir: 'past',
      unit: instance.config.duration_unit,
      calendar,
    });

    if (workEnd.valueOf() < workStart.valueOf()) {
      workEnd = instance.date.add(workEnd, 1, 'day');
    }

    if (instance.date.date_part(instance.copy(workStart))
      .valueOf() !== instance.date.date_part(instance.copy(workEnd))
      .valueOf()) {
      return {
        firstHour: 0,
        lastHour: 0,
        hoursPerDay: 0,
      };
    }

    return {
      firstHour: workStart.getHours(),
      lastHour: workEnd.getHours(),
      hoursPerDay: workEnd.getHours() - workStart.getHours(),
    };
  };

  instance.getRelativeEventPosition = function (ev, node) {
    const d = document.documentElement;
    const pos = {
      x: ev.clientX + d.scrollLeft - d.clientLeft,
      y: ev.clientY + d.scrollTop - d.clientTop,
    };

    const box = instance.elementPosition(node);

    pos.x = pos.x - box.x + node.scrollLeft;
    pos.y = pos.y - box.y + node.scrollTop;

    return pos;
  };

  instance.elementPosition = function (elem) {
    let top = 0;
    let left = 0;
    let right = 0;
    let
      bottom = 0;

    if (elem.getBoundingClientRect) { // HTML5 method
      const box = elem.getBoundingClientRectWrapper();
      const body = document.body;
      const docElem = (document.documentElement
        || document.body.parentNode
        || document.body);

      const scrollTop = window.pageYOffset || docElem.scrollTop || body.scrollTop;
      const scrollLeft = window.pageXOffset || docElem.scrollLeft || body.scrollLeft;
      const clientTop = docElem.clientTop || body.clientTop || 0;
      const clientLeft = docElem.clientLeft || body.clientLeft || 0;

      top = box.top + scrollTop - clientTop;
      left = box.left + scrollLeft - clientLeft;

      right = document.body.offsetWidth - box.right;
      bottom = document.body.offsetHeight - box.bottom;
    } else { // fallback to naive approach
      while (elem) {
        top += parseInt(elem.offsetTop, 10);
        left += parseInt(elem.offsetLeft, 10);
        elem = elem.offsetParent;
      }

      right = document.body.offsetWidth - elem.offsetWidth - left;
      bottom = document.body.offsetHeight - elem.offsetHeight - top;
    }

    return {
      y: Math.round(top),
      x: Math.round(left),
      width: elem.offsetWidth,
      height: elem.offsetHeight,
      right: Math.round(right),
      bottom: Math.round(bottom),
    };
  };

  // instance._task_default_render = function (task) {
  //   if (this._isAllowedUnscheduledTask(task)) {
  //     return;
  //   }
  //
  //   var pos = this._get_task_pos(task);
  //
  //   var cfg = this.config;
  //   var height = this._get_task_height();
  //
  //   var padd = Math.floor((this.config.row_height - height) / 2);
  //
  //   if (this._get_safe_type(task.type) == cfg.types.milestone && cfg.link_line_width > 1) {
  //     //little adjust milestone position, so horisontal corners would match link arrow when thickness of link line is more than 1px
  //     padd += 1;
  //   }
  //
  //   var div = document.createElement("div");
  //   var width = instance._get_task_width(task);
  //   var type = this._get_safe_type(task.type);
  //
  //   div.setAttribute(this.config.task_attribute, task.id);
  //
  //   if (cfg.show_progress && type != this.config.types.milestone) {
  //     this._render_task_progress(task, div, width);
  //   }
  //
  //   //use separate div to display content above progress bar
  //   var content = instance._render_task_content(task, width);
  //
  //   if (task.textColor) {
  //     content.style.color = task.textColor;
  //   }
  //
  //   div.appendChild(content);
  //
  //   var css = this._combine_item_class(
  //     "gantt_task_line",
  //     this.templates.task_class(task.start_date, task.end_date, task),
  //     task.id
  //   );
  //
  //   if (task.color || task.progressColor || task.textColor) {
  //     css += " gantt_task_inline_color";
  //   }
  //
  //   div.className = css;
  //
  //   var styles = [
  //     "left:" + pos.x + "px",
  //     "top:" + (padd + pos.y) + 'px',
  //     "height:" + height + 'px',
  //     "line-height:" + (Math.max(height < 30 ? height - 2 : height, 0)) + 'px',
  //     "width:" + width + 'px'
  //   ];
  //
  //   if (task.color) {
  //     styles.push("background-color:" + task.color);
  //   }
  //
  //   if (task.textColor) {
  //     styles.push("color:" + task.textColor);
  //   }
  //
  //   div.style.cssText = styles.join(";");
  //
  //   var side = this._render_leftside_content(task);
  //
  //   if (side) {
  //     div.appendChild(side);
  //   }
  //
  //   side = this._render_rightside_content(task);
  //
  //   if (side) {
  //     div.appendChild(side);
  //   }
  //
  //   instance._waiAria.setTaskBarAttr(task, div);
  //
  //   if (!this._is_readonly(task)) {
  //     if (cfg.drag_resize && type != this.config.types.milestone) {
  //       if (!this._is_flex_task(task)) {
  //         instance._render_pair(div, "gantt_task_drag", task, function (css) {
  //           var el = document.createElement("div");
  //           el.className = css;
  //           return el;
  //         });
  //       } else {
  //         instance._render_pair(div, "gantt_task_no_drag tooltip-gantt", task, function (css) {
  //           var el = document.createElement("div");
  //           el.setAttribute('data-key', "gantt_parent_no_drug");
  //           el.className = css;
  //           return el;
  //         });
  //       }
  //
  //     }
  //
  //     if (cfg.drag_links && this.config.show_links) {
  //       instance._render_pair(div, "gantt_link_control", task, function (css) {
  //         var outer = document.createElement("div");
  //
  //         outer.className = css;
  //         outer.style.cssText = [
  //           "height:" + height + 'px',
  //           "line-height:" + height + 'px'
  //         ].join(";");
  //
  //         var inner = document.createElement("div");
  //
  //         inner.className = "gantt_link_point";
  //         inner.style.display = instance._show_link_points ? "block" : "";
  //         outer.appendChild(inner);
  //
  //         return outer;
  //       });
  //     }
  //   }
  //
  //   return div;
  // };

  instance.customTimelineTaskIdChange = (id, newId) => {
    const timeline = instance.$ui.getView('timeline');

    if (timeline._tasks_dnd.dragMultiple[id]) {
      timeline._tasks_dnd.dragMultiple[newId] = timeline._tasks_dnd.dragMultiple[id];
      delete timeline._tasks_dnd.dragMultiple[id];

      if (timeline._tasks_dnd.dragMultiple[newId].id) {
        timeline._tasks_dnd.dragMultiple[newId].id = newId;
      }

      if (timeline._tasks_dnd.dragMultiple[newId].obj && timeline._tasks_dnd.dragMultiple[newId].obj.id) {
        timeline._tasks_dnd.dragMultiple[newId].obj.id = newId;
      }
    }
  };

  instance.disableTimelineReadonly = () => {
    const timeline = instance.$ui.getView('timeline');

    if (timeline && timeline._timelineDragScroll?._originalReadonly) {
      timeline._timelineDragScroll._originalReadonly = false;
    }
  };

  instance.initCustomTimeline = function () {
    const timeline = instance.$ui.getView('timeline');
    const getPos = timeline.getItemPosition;
    const getBarHeight = timeline.getBarHeight;
    const dndCoords = timeline._tasks_dnd._drag_task_coords;

    timeline.getItemPosition = function (task, start_date, end_date) {
      const state = instance.getState();

      let position = getPos.apply(this, arguments);

      if (+task.id !== +state.drag_id) {
        position = getPos.call(this, instance.__scaleTask(task), start_date, end_date);
      } else {
        const isProgressOrResize = _.includes(['progress', 'resize'], state.drag_mode);

        if (isProgressOrResize && +state.drag_id === +task.id) {
          const copy = instance.__scaleTask(task);

          if (state.drag_mode === 'resize') {
            if (state.drag_from_start) {
              copy.start_date = task.start_date;
            } else {
              copy.end_date = task.end_date;
            }
          }

          position = getPos.call(this, copy, start_date, end_date);
        }
      }

      if (task.type === 'project') {
        position.top += 8;
      }

      return position;
    };

    timeline.getBarHeight = function (taskId, isMilestoneRender) {
      let height = getBarHeight.apply(this, arguments);

      if (isMilestoneRender) {
        height = Math.floor(height * Math.sqrt(2));
      }

      return height;
    };

    timeline._tasks_dnd._drag_task_coords = function (task, dnd) {
      return dndCoords.call(this, instance.__scaleTask(task), dnd);
    };

    timeline._tasks_dnd._fix_dnd_scale_time = function (task, drag) {
      let unit = instance.getScale().unit;
      let step = instance.getScale().step;

      if (!instance.config.round_dnd_dates) {
        unit = 'minute';
        step = instance.config.time_step;
      }

      function fixStart(_task) {
        if (!instance.isWorkTime(_task.start_date, undefined, _task)) {
          _task.start_date = instance.getClosestWorkTime({
            date: _task.start_date,
            dir: 'future',
            task: _task,
          });
        }
      }

      function fixEnd(_task) {
        if (!instance.isWorkTime(_task.end_date, undefined, _task)) {
          if (new Date(_task.end_date).valueOf() <= new Date(_task.start_date).valueOf()) {
            _task.end_date = instance.calculateEndDate(_task.start_date, 1);
          } else {
            _task.end_date = instance.getClosestWorkTime({
              date: _task.end_date,
              dir: unit === 'hour' ? 'future' : 'past',
              task: _task,
            });
          }
        }
      }

      if (drag.mode === instance.config.drag_mode.resize) {
        if (drag.left) {
          task.start_date = instance.roundDate({
            date: task.start_date,
            unit,
            step,
          });
          fixStart(task);
        } else {
          task.end_date = instance.roundDate({
            date: task.end_date,
            unit,
            step,
          });
          fixEnd(task);
        }
      } else if (drag.mode === instance.config.drag_mode.move) {
        task.start_date = instance.roundDate({
          date: task.start_date,
          unit,
          step,
        });
        fixStart(task);

        task.end_date = instance.calculateEndDate({
          start_date: task.start_date,
          duration: task.duration,
          task,
          unit: instance.config.duration_unit,
        });
      }
    };

    timeline._tasks_dnd._update_on_move = function (e) {
      const drag = this.drag;
      const config = timeline.$getConfig();

      if (drag.mode) {
        const pos = instance.getRelativeEventPosition(e, instance.$task_data);

        if (drag.pos && drag.pos.x === pos.x) return;

        drag.pos = pos;

        const curr_date = instance.dateFromPos(pos.x);

        if (!curr_date || Number.isNaN(curr_date.getTime())) return;

        let shift = pos.x - drag.start_x;
        const ev = instance.getTask(drag.id);

        if (this._handlers[drag.mode]) {
          if (instance.config.drag_project && drag.mode === config.drag_mode.move && ev && instance.isSummaryTask(ev)) {
            const initialDrag = {};

            initialDrag[drag.id] = instance.copy(drag);

            const dragMultiple = this.dragMultiple;

            _.each(dragMultiple, (obj, key) => {
              if (instance.getTask(key).type === instance.config.types.button) {
                delete dragMultiple[key];
              }
            });

            const maxShift = this._find_max_shift(instance.mixin(initialDrag, this.dragMultiple), shift);

            if (maxShift !== undefined) {
              shift = maxShift;
            }

            this._update_item_on_move(shift, drag.id, drag.mode, drag, e);

            _.each(Object.keys(this.dragMultiple), key => {
              const childDrag = this.dragMultiple[key];

              this._update_item_on_move(shift, childDrag.id, childDrag.mode, childDrag, e);
            });
          } else {
            this._update_item_on_move(shift, drag.id, drag.mode, drag, e);
          }
        }
      }
    };

    timeline._tasks_dnd.on_mouse_move = function (e) {
      const drag = this.drag;

      if (drag.start_drag) {
        const task = instance.getTask(drag.start_drag.id);
        const ganttId = task.gantt_id;
        const canMoveAndResizeTask = rights.project.hasRight(ganttId, 'static_fields');
        const canChangeProgressTask = rights.project.hasRightSomeOne(ganttId, ['static_fields', 'static_fields_4_only']);

        if (
          (drag.start_drag.mode === instance.config.drag_mode.move && !canMoveAndResizeTask)
          || (drag.start_drag.mode === instance.config.drag_mode.resize && !canMoveAndResizeTask)
          || (drag.start_drag.mode === instance.config.drag_mode.progress && !canChangeProgressTask)
        ) {
          return;
        }

        this._start_dnd(e);
      }

      if (drag.mode) {
        if (!instance._checkTimeout(this, 30)) { // limit update frequency
          return;
        }

        this._update_on_move(e);
      }
    };

    timeline._tasks_dnd._finalize_mouse_up = function (taskId, config, drag, e) {
      const task = instance.getTask(taskId);

      if (config.work_time && config.correct_work_time) {
        this._fix_working_times(task, drag);
      }

      this._fix_dnd_scale_time(task, drag);

      if (!this._fireEvent('before_finish', drag.mode, [taskId, drag.mode, instance.copy(drag.obj), e])) {
        this.clear_drag_state();
        if (taskId === drag.id) {
          drag.obj._dhx_changed = false;
          instance.mixin(task, drag.obj, true);
        }
        instance.refreshTask(taskId);

        return false;
      }

      const drag_id = taskId;

      instance._init_task_timing(task);
      this.clear_drag_state();
      this._fireEvent('after_finish', drag.mode, [drag_id, drag.mode, e]);

      return true;
    };

    timeline._tasks_dnd.on_mouse_up = function (e) {
      const drag = this.drag;

      if (drag.mode && drag.id) {
        const config = timeline.$getConfig();
        const tasksToUpdateIDs = [];

        // if (drag.obj.type === constants.TASK_TYPES.project) {
        //   instance._preventCustomAutoscheduling = true;
        // }

        // drop
        const ev = instance.getTask(drag.id);
        const dragMultiple = this.dragMultiple;
        let dragProject = false;

        instance.updateTask(drag.id);

        if (instance.isSummaryTask(ev) && config.drag_project && drag.mode === config.drag_mode.move) {
          _.each(Object.keys(dragMultiple), key => {
            this._finalize_mouse_up(dragMultiple[key].id, config, dragMultiple[key], e);
            tasksToUpdateIDs.push(dragMultiple[key].id);
            dragProject = true;
          });
        }

        this._finalize_mouse_up(drag.id, config, drag, e);

        if (drag && drag.obj) {
          drag.obj.start_date = ev.start_date;
          drag.obj.end_date = ev.end_date;
          drag.obj.duration = ev.duration;
        }

        drag.mode === config.drag_mode.move && userExtAnalytics.log('gantt_task_timeline_drop');
        drag.mode === config.drag_mode.progress && userExtAnalytics.log('gantt_task_timeline_progress_changed');
        drag.mode === config.drag_mode.resize && userExtAnalytics.log('gantt_task_timeline_resize');

        // if (drag.mode === config.drag_mode.progress) {
        //   instance.callEvent('changeProgress', [updatedTask]);
        // } else {
        //   !dragProject && instance.updateTask(drag.id);
        // }

        if (!dragProject) {
          instance.updateTask(drag.id);
        } else {
          let parent = drag.obj.parent;

          while (parent && parent !== 1) {
            instance.updateTask(parent);
            parent = instance.getTask(parent).parent;
          }
        }

        if (tasksToUpdateIDs.length > 0) {
          instance.batchUpdate(() => {
            tasksToUpdateIDs.forEach(id => {
              const task = instance.getTask(id);

              if (task.type !== constants.TASK_TYPES.project) {
                instance.updateTask(task.id);
              }
            });
          });
        }
      }

      // delete instance._preventCustomAutoscheduling;

      this.clear_drag_state();
    };
  };
}
